import { isDefined } from "@clipboard-health/util-ts";
import { TextField } from "@mui/material";
import { useEffectivePermissions } from "@src/appV2/Auth/hooks/useEffectivePermissions";
import { useFormContext } from "react-hook-form";

export const MODIFICATION_REASON_FIELD_NAME = "modificationReason";
export const MODIFICATION_REASON_LABEL = "Reason for change";
export const MODIFICATION_REASON_HELPER_TEXT = "Required for audit log";
export const MODIFICATION_REASON_MAX_LENGTH = 500;

export function isModificationReasonValid(value: string): boolean {
  return value.trim().length > 0;
}

// True only when the acting user has CBH admin privileges. `hasAdminPrivileges`
// persists during pseudo-login, so this also covers CBH employees impersonating
// a workplace user. Native workplace users acting under their own account get false.
export function useIsModificationReasonRequired(): boolean {
  return useEffectivePermissions().hasAdminPrivileges;
}

export function useIsModificationReasonSatisfied(value: string): boolean {
  const isRequired = useIsModificationReasonRequired();
  return !isRequired || isModificationReasonValid(value);
}

export function FormModificationReasonField() {
  const isRequired = useIsModificationReasonRequired();
  const {
    register,
    formState: { errors },
  } = useFormContext();

  if (!isRequired) {
    return null;
  }

  const error = errors[MODIFICATION_REASON_FIELD_NAME];

  return (
    <TextField
      fullWidth
      multiline
      minRows={3}
      maxRows={6}
      label={MODIFICATION_REASON_LABEL}
      inputProps={{
        maxLength: MODIFICATION_REASON_MAX_LENGTH,
        "data-dd-privacy": "mask",
      }}
      {...register(MODIFICATION_REASON_FIELD_NAME, {
        required: "Modification reason is required",
        validate: (value: unknown) =>
          isModificationReasonValid(typeof value === "string" ? value : "") ||
          "Modification reason is required",
      })}
      error={isDefined(error)}
      helperText={
        typeof error?.message === "string" && error.message.length > 0
          ? error.message
          : MODIFICATION_REASON_HELPER_TEXT
      }
    />
  );
}

interface ControlledModificationReasonFieldProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export function ControlledModificationReasonField(props: ControlledModificationReasonFieldProps) {
  const { value, onChange, disabled } = props;
  const isRequired = useIsModificationReasonRequired();

  if (!isRequired) {
    return null;
  }

  return (
    <TextField
      fullWidth
      multiline
      required
      minRows={3}
      maxRows={6}
      disabled={disabled}
      label={MODIFICATION_REASON_LABEL}
      helperText={MODIFICATION_REASON_HELPER_TEXT}
      inputProps={{
        maxLength: MODIFICATION_REASON_MAX_LENGTH,
        "data-dd-privacy": "mask",
      }}
      value={value}
      onChange={(event) => {
        onChange(event.target.value);
      }}
    />
  );
}
